import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last thing standing between a render error and a black screen (§65).
 *
 * ============================ WHY THIS EXISTS ============================
 * There was no error boundary anywhere in the Console. React's documented
 * behaviour when an error escapes rendering is to unmount the WHOLE tree, so a
 * single exception in one row of the chat feed replaced the entire application
 * with an empty page — no message, no nav, no way back except a reload, and
 * nothing on screen naming what broke.
 *
 * That is exactly how it was reported: "the chat screen is broken, goes dark
 * and I can't see nothing." Diagnosing it needed DevTools, because the product
 * itself had thrown away the one piece of information that mattered.
 *
 * So this shows the error. Not a shrug — the actual name, message and the
 * component stack that says WHERE. This is a private operator console, the
 * operator is authenticated, and the alternative is asking them to open
 * DevTools and read it to us.
 *
 * WHAT THIS DOES NOT CATCH, and no error boundary does: errors thrown in event
 * handlers, in `setTimeout`, in promises that nobody awaits, or during server
 * rendering. React only routes errors here when they escape RENDER, a lifecycle
 * method, or a constructor. A blank screen with this component installed is
 * therefore still possible, and still worth reporting.
 * =======================================================================
 */

type Props = {
  children: ReactNode;
  /**
   * Names the surface that failed, so the message can say "the chat page"
   * rather than "the application".
   */
  surface?: string;
  /**
   * Changing this resets the boundary.
   *
   * Passed the route path by the page-level boundary: without it, one crash
   * would leave the error showing for every subsequent page, because a
   * boundary that has caught stays caught until its state is cleared. The
   * operator would navigate to Settings and still see the chat error.
   */
  resetKey?: string;
};

type State = {
  error: Error | undefined;
  componentStack: string | undefined;
};

/**
 * Anything can be thrown in JavaScript — `throw 'boom'` is legal, and so is
 * `throw undefined`. Normalising here means the render below can rely on
 * having a name and a message instead of guarding every field.
 */
function asError(thrown: unknown): Error {
  if (thrown instanceof Error) return thrown;
  return new Error(typeof thrown === 'string' ? thrown : `Non-error thrown: ${String(thrown)}`);
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined, componentStack: undefined };

  static getDerivedStateFromError(thrown: unknown): Partial<State> {
    return { error: asError(thrown) };
  }

  override componentDidUpdate(previous: Props): void {
    // Navigating away clears the error. Compared against the PREVIOUS key
    // rather than stored in state, so there is one source of truth for it.
    if (this.state.error !== undefined && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined, componentStack: undefined });
    }
  }

  override componentDidCatch(thrown: unknown, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? undefined });

    /*
     * Also logged, so the browser console keeps the real stack with its source
     * map. What renders below is readable; this is complete.
     */
    // eslint-disable-next-line no-console
    console.error('[Connaro] render error', asError(thrown), info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ error: undefined, componentStack: undefined });
  };

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (error === undefined) return this.props.children;

    const surface = this.props.surface ?? 'this page';

    return (
      <section className="panel error-boundary" role="alert" aria-live="assertive">
        <div className="panel__body">
          <h2 className="error-boundary__title">Something in {surface} failed to render</h2>

          <p className="error-boundary__lede">
            The rest of the Console is unaffected. Nothing was lost — this is a display fault, not a
            change to your data.
          </p>

          {/*
           * The error itself, verbatim. React escapes it as text, and it is the
           * one thing that makes the fault reportable without DevTools.
           */}
          <pre className="error-boundary__message">
            {error.name}: {error.message}
          </pre>

          {componentStack === undefined ? null : (
            <details className="error-boundary__details">
              <summary>Where it happened</summary>
              <pre className="error-boundary__stack">{componentStack.trim()}</pre>
            </details>
          )}

          <div className="error-boundary__actions">
            <button type="button" className="btn btn--sm" onClick={this.retry}>
              Try again
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload the Console
            </button>
          </div>
        </div>
      </section>
    );
  }
}
